import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { NotesAPI } from '@/lib/notesApi';
import { useOrganization } from './ClerkOrganizationContext';
import { useUser } from '@clerk/clerk-react';
import type { Note, NoteTemplate, CreateNoteRequest } from '@/types/note';

interface NotesContextType {
  notes: Note[];
  noteTemplates: NoteTemplate[];
  loading: boolean;
  error: string | null;
  createNote: (note: CreateNoteRequest) => Promise<Note>;
  updateNote: (id: string, updates: Partial<Note>) => Promise<Note>;
  deleteNote: (id: string) => Promise<void>;
  createNoteTemplate: (template: Omit<NoteTemplate, 'id' | 'created_at' | 'updated_at'>) => Promise<NoteTemplate>;
  updateNoteTemplate: (id: string, updates: Partial<NoteTemplate>) => Promise<NoteTemplate>;
  deleteNoteTemplate: (id: string) => Promise<void>;
  refreshNotes: () => Promise<void>;
}

const NotesContext = createContext<NotesContextType | undefined>(undefined);

export function NotesProvider({ children }: { children: ReactNode }) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [noteTemplates, setNoteTemplates] = useState<NoteTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { user } = useUser();
  const { currentOrganization } = useOrganization();

  const refreshNotes = async () => {
    try {
      if (!currentOrganization?.id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      const [notesData, templatesData] = await Promise.all([
        NotesAPI.getNotes(currentOrganization.id),
        NotesAPI.getNoteTemplates(currentOrganization.id),
      ]);
      
      setNotes(notesData);
      setNoteTemplates(templatesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load notes';
      setError(errorMessage);
      // console.error('Error loading notes:', err);
      // Don't crash the app, just show empty state
      setNotes([]);
      setNoteTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshNotes();
  }, [currentOrganization?.id]);

  const createNote = async (noteData: CreateNoteRequest): Promise<Note> => {
    if (!currentOrganization?.id || !user?.id) {
      throw new Error('Organization or user not found');
    }

    const noteToCreate = {
      ...noteData,
      organization_id: currentOrganization.id,
      created_by: user.id,
    };

    const newNote = await NotesAPI.createNote(noteToCreate);
    setNotes(prev => [newNote, ...prev]);
    return newNote;
  };

  const updateNote = async (id: string, updates: Partial<Note>): Promise<Note> => {
    const updatedNote = await NotesAPI.updateNote(id, updates);
    setNotes(prev => prev.map(note => note.id === id ? updatedNote : note));
    return updatedNote;
  };

  const deleteNote = async (id: string): Promise<void> => {
    await NotesAPI.deleteNote(id);
    setNotes(prev => prev.filter(note => note.id !== id));
  };

  const createNoteTemplate = async (templateData: Omit<NoteTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<NoteTemplate> => {
    if (!currentOrganization?.id || !user?.id) {
      throw new Error('Organization or user not found');
    }

    const templateToCreate = {
      ...templateData,
      organization_id: currentOrganization.id,
      created_by: user.id,
    };

    const newTemplate = await NotesAPI.createNoteTemplate(templateToCreate);
    setNoteTemplates(prev => [...prev, newTemplate]);
    return newTemplate;
  };

  const updateNoteTemplate = async (id: string, updates: Partial<NoteTemplate>): Promise<NoteTemplate> => {
    const updatedTemplate = await NotesAPI.updateNoteTemplate(id, updates);
    setNoteTemplates(prev => prev.map(template => template.id === id ? updatedTemplate : template));
    return updatedTemplate;
  };

  const deleteNoteTemplate = async (id: string): Promise<void> => {
    await NotesAPI.deleteNoteTemplate(id);
    setNoteTemplates(prev => prev.filter(template => template.id !== id));
  };

  return (
    <NotesContext.Provider value={{
      notes,
      noteTemplates,
      loading,
      error,
      createNote,
      updateNote,
      deleteNote,
      createNoteTemplate,
      updateNoteTemplate,
      deleteNoteTemplate,
      refreshNotes,
    }}>
      {children}
    </NotesContext.Provider>
  );
}

export function useNotes() {
  const context = useContext(NotesContext);
  if (context === undefined) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
}